import {
  Box,
  Divider,
  Flex,
  Icon,
  IconName,
  MobileDataTableCardSurface,
  MobileDataTableGroupCardSurface,
  Text,
  type MobileGroupCardRendererProps,
} from '@talos/kyoko';
import type { ExpandedChangedEvent, IRowNode } from 'ag-grid-community';
import { useCallback, useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  CardMetricWrapper,
  CardMetrics,
  GroupMetricWrapper,
  InstrumentCell,
  MarketAccountText,
  WarningCell,
} from './styles';

export interface MobileBlotterCardMetric {
  colId: string;
  label: string;
  value: ReactNode;
  testId: string;
}

interface MobileBlotterCardProps {
  compact?: boolean;
  onPress?: () => void;
  testId: string;
  instrumentTestId: string;
  marketAccountTestId: string;
  warningTestId: string;
  instrument: ReactNode;
  marketAccount: ReactNode;
  warning?: ReactNode;
  primaryMetric: MobileBlotterCardMetric;
  compactMetric: MobileBlotterCardMetric;
  secondaryMetrics: MobileBlotterCardMetric[];
}

export function MobileBlotterCard({
  compact = false,
  onPress,
  testId,
  instrumentTestId,
  marketAccountTestId,
  warningTestId,
  instrument,
  marketAccount,
  warning,
  primaryMetric,
  compactMetric,
  secondaryMetrics,
}: MobileBlotterCardProps) {
  return (
    <MobileDataTableCardSurface
      type="button"
      data-testid={testId}
      onClick={onPress}
      $background="inherit"
      $compact={compact}
    >
      <Flex alignItems="center" gap="spacing8" overflow="hidden">
        <InstrumentCell data-testid={instrumentTestId} overflow="hidden">
          {instrument}
        </InstrumentCell>
        {!compact && marketAccount != null && marketAccount !== '' ? (
          <MarketAccountText data-testid={marketAccountTestId}>{marketAccount}</MarketAccountText>
        ) : null}
        {warning != null ? <WarningCell data-testid={warningTestId}>{warning}</WarningCell> : null}
        <MobileBlotterCardMetricItem metric={compact ? compactMetric : primaryMetric} />
      </Flex>

      {!compact ? <Divider color="backgroundDivider" /> : null}

      {!compact ? (
        <CardMetrics>
          {secondaryMetrics.map(metric => (
            <MobileBlotterCardMetricItem key={metric.colId} metric={metric} />
          ))}
        </CardMetrics>
      ) : null}
    </MobileDataTableCardSurface>
  );
}

function MobileBlotterCardMetricItem({ metric }: { metric: MobileBlotterCardMetric }) {
  return (
    <CardMetricWrapper>
      <Text color="colorTextSubtle" fontSize="fontSizeSm">
        {metric.label}
      </Text>
      <Box data-testid={metric.testId} color="colorTextImportant">
        {metric.value}
      </Box>
    </CardMetricWrapper>
  );
}

interface MobileBlotterGroupCardProps {
  node: IRowNode;
  testId: string;
  groupTestId: string;
  groupMetricCell: ReactNode;
  warning?: ReactNode;
  warningTestId?: string;
  valueMetric?: Pick<MobileBlotterCardMetric, 'label' | 'value' | 'testId'> | null;
  showExpandIndicator?: boolean;
}

export function MobileBlotterGroupCard({
  node,
  testId,
  groupTestId,
  groupMetricCell,
  warning,
  warningTestId,
  valueMetric,
  showExpandIndicator = false,
}: MobileBlotterGroupCardProps) {
  const [expanded, setExpanded] = useState(node.expanded);

  useEffect(() => {
    const listener = (event: ExpandedChangedEvent) => setExpanded(event.node.expanded);
    node.addEventListener('expandedChanged', listener);
    return () => node.removeEventListener('expandedChanged', listener);
  }, [node]);

  const onToggle = useCallback(() => node.setExpanded(!node.expanded), [node]);
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    },
    [onToggle]
  );

  return (
    <MobileDataTableGroupCardSurface
      onClick={onToggle}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      data-testid={testId}
      expanded={expanded}
    >
      <Flex alignItems="center" gap="spacing8" overflow="hidden" flex="1 1 0">
        {showExpandIndicator ? <Icon icon={IconName.ChevronRight} rotate={expanded ? 90 : undefined} /> : null}
        <GroupMetric value={groupMetricCell} testId={groupTestId} />
        {warning != null ? <WarningCell data-testid={warningTestId}>{warning}</WarningCell> : null}
      </Flex>
      {valueMetric ? (
        <GroupMetric label={valueMetric.label} value={valueMetric.value} testId={valueMetric.testId} align="flex-end" />
      ) : null}
    </MobileDataTableGroupCardSurface>
  );
}

interface ResolveGroupMetricCellParams {
  colId: string | null;
  fallbackLabel: string;
  renderCell: MobileGroupCardRendererProps['renderCell'];
}

/**
 * Always mounts the grouped column's cell renderer first (same as desktop auto-group).
 * Row-group columns are often hidden in the column panel but must still use their
 * renderer (e.g. `asset` / `currencyColumn`). Plain text is only used when the
 * renderer and its formatted fallback return nothing useful.
 */
export function resolveGroupMetricCell({ colId, fallbackLabel, renderCell }: ResolveGroupMetricCellParams): ReactNode {
  if (colId == null) {
    return fallbackLabel;
  }

  const rendered = renderCell(colId, { overrideColumnVisibility: true });
  if (rendered != null && rendered !== false && rendered !== '') {
    return rendered;
  }

  return fallbackLabel;
}

interface GroupMetricProps {
  label?: string;
  value: ReactNode;
  testId: string;
  align?: 'flex-start' | 'flex-end';
}

function GroupMetric({ label, value, testId, align = 'flex-start' }: GroupMetricProps) {
  return (
    <GroupMetricWrapper alignItems={align}>
      {label ? (
        <Text color="colorTextSubtle" fontSize="fontSizeSm">
          {label}
        </Text>
      ) : null}
      <Flex data-testid={testId} color="colorTextImportant" className="group-metric-value">
        {value}
      </Flex>
    </GroupMetricWrapper>
  );
}
